/**
 * Statuses API Client
 *
 * Manages tenant-configurable task statuses.
 */

import apiClient from './client';

export interface TenantStatus {
  id: string;
  key: string;
  label: string;
  color: string;
  bgColor: string | null;
  isDefault: boolean;
  isTerminal: boolean;
  isActive: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateStatusRequest {
  key: string;
  label: string;
  color: string;
  bg_color?: string;
  is_default?: boolean;
  is_terminal?: boolean;
  sort_order?: number;
}

export interface UpdateStatusRequest {
  label?: string;
  color?: string;
  bg_color?: string | null;
  is_default?: boolean;
  is_terminal?: boolean;
  is_active?: boolean;
  sort_order?: number;
}

const BASE = '/v1/statuses';

export const statusesApi = {
  /**
   * List all statuses for current tenant
   */
  list: async (includeInactive = false): Promise<TenantStatus[]> => {
    const params = includeInactive ? { include_inactive: 'true' } : {};
    const response = await apiClient.get<{ items: TenantStatus[] }>(BASE, { params });
    return response.data.items;
  },

  /**
   * Get single status by ID
   */
  get: async (id: string): Promise<TenantStatus> => {
    const response = await apiClient.get<TenantStatus>(`${BASE}/${id}`);
    return response.data;
  },

  /**
   * Create new status
   */
  create: async (data: CreateStatusRequest): Promise<TenantStatus> => {
    const response = await apiClient.post<TenantStatus>(BASE, data);
    return response.data;
  },

  /**
   * Update status
   */
  update: async (id: string, data: UpdateStatusRequest): Promise<TenantStatus> => {
    const response = await apiClient.put<TenantStatus>(`${BASE}/${id}`, data);
    return response.data;
  },

  /**
   * Reorder statuses
   */
  reorder: async (statusIds: string[]): Promise<void> => {
    await apiClient.post(`${BASE}/reorder`, { status_ids: statusIds });
  },

  /**
   * Delete status
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`${BASE}/${id}`);
  },
};
